/**
 * Shared boilerplate for the theme's Three.js-powered sections: script
 * loading, feature detection and visibility/resize plumbing that every
 * WebGL section (network-background, hero-3d, cinematic-intro, ...) needs.
 * Exposed as a single global so it can be loaded with a plain <script> tag
 * ahead of a section's own {% javascript %} block, matching the rest of
 * this theme (no bundler / module system).
 */
(() => {
  const loadScript = (src) =>
    new Promise((resolve, reject) => {
      if (!src) {
        reject(new Error('three-scene-utils: missing script src'));
        return;
      }
      const existing = document.querySelector(`script[src="${src}"]`);
      if (existing) {
        if (existing.dataset.loaded === 'true') {
          resolve();
        } else {
          existing.addEventListener('load', () => resolve());
          existing.addEventListener('error', reject);
        }
        return;
      }
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => {
        script.dataset.loaded = 'true';
        resolve();
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });

  const supportsWebGL = () => {
    try {
      const c = document.createElement('canvas');
      return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl')));
    } catch (e) {
      return false;
    }
  };

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const capDPR = (max = 2) => Math.min(window.devicePixelRatio || 1, max);

  // Combines IntersectionObserver (in-viewport) and the page's
  // visibilitychange (tab backgrounded) into a single onChange(visible)
  // callback, so render loops can pause when either is true.
  const observeVisibility = (el, onChange) => {
    let inViewport = true;
    let tabVisible = !document.hidden;

    const emit = () => onChange(inViewport && tabVisible);

    const io = new IntersectionObserver(
      (entries) => {
        inViewport = entries[0].isIntersecting;
        emit();
      },
      { threshold: 0 }
    );
    io.observe(el);

    const onVisibilityChange = () => {
      tabVisible = !document.hidden;
      emit();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      io.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  };

  window.ThreeSceneUtils = {
    loadScript,
    supportsWebGL,
    prefersReducedMotion,
    capDPR,
    observeVisibility,
  };
})();
